<template>
  <div class="adauga-factura-iesire-vertical d-flex flex-column gap-4">
    <div class="d-flex justify-content-between align-items-center">
      <h5 class="fw-bold mb-0">Adaugă factură ieșire</h5>
      <slot name="actions"></slot>
    </div>

    <div class="d-flex flex-column gap-3">
      <!-- Serie + număr + Client -->
      <div class="row g-3 align-items-end">
        <!-- Serie + număr -->
        <div class="col-12 col-md-3">
          <label class="form-label mb-1">Serie și număr</label>
          <input v-model="smartSerieNr" @input="onSmartSerieNrInput" type="text" class="form-control"
            placeholder="Ex: F12345 (Serie + Număr)">
        </div>

        <!-- Tip Document -->
        <div class="col-12 col-md-3">
          <label class="form-label mb-1">Tip document</label>
          <multiselect v-model="tipDocObj" :options="tipuriDocument" track-by="id" label="descriere"
            placeholder="Tip document" class="w-100" :open-direction="'bottom'" :select-label="''" :deselect-label="''"
            :selected-label="''" />
        </div>

        <!-- Client -->
        <div class="col-12 col-md-6">
          <label class="form-label mb-1">Client</label>
          <multiselect id="id_client" v-model="clientObj" :options="listaClienti" :custom-label="clientLabel"
            track-by="id" placeholder="Client" class="w-100" :open-direction="'bottom'" :select-label="''"
            :deselect-label="''" :selected-label="''" />
        </div>
      </div>

      <!-- Date + Valută + Curs + Sold (same row) -->
      <div class="row g-3 align-items-end">
        <div class="col-12 col-md-3">
          <label class="form-label mb-1">Data emiterii</label>
          <div class="position-relative">
            <input ref="dateEmitereRef" v-model="factura.data_emitere" type="date" class="form-control pe-5" />
            <button type="button" class="date-picker-btn btn btn-outline-secondary btn-sm"
              @click="openNativePicker('emitere')" aria-label="Deschide calendar">
              📅
            </button>
          </div>
        </div>

        <div class="col-12 col-md-3">
          <label class="form-label mb-1">Data scadenței</label>
          <div class="position-relative">
            <input ref="dateScadentaRef" v-model="factura.termen_plata" type="date" class="form-control pe-5"
              @input="termenPlataManual = true" />
            <button type="button" class="date-picker-btn btn btn-outline-secondary btn-sm"
              @click="openNativePicker('scadenta')" aria-label="Deschide calendar">
              📅
            </button>
          </div>
        </div>

        <div class="col-12 col-md-2">
          <label class="form-label mb-1">Valută</label>
          <multiselect v-model="valutaObj" :options="valute" :custom-label="valutaLabel" track-by="id"
            placeholder="Valută" class="w-100" :open-direction="'bottom'" :select-label="''" :deselect-label="''"
            :selected-label="''" />
        </div>

        <div class="col-12 col-md-2">
          <label class="form-label mb-1">Curs valutar</label>
          <input v-model="factura.curs_valutar" type="number" step="0.0001" class="form-control" placeholder="Curs BNR">
        </div>

        <div class="col-12 col-md-2">
          <label class="form-label mb-1">Sold client</label>
          <input :value="formattedSold" type="text" class="form-control" disabled>
        </div>
      </div>

      <!-- Buton afișare detalii suplimentare -->
      <div class="mt-2">
        <button type="button" class="btn btn-sm btn-light btn-outline-secondary" @click="showMore = !showMore">
          {{ showMore ? 'Ascunde detalii' : 'Afișează mai mult' }}
        </button>
      </div>

      <template v-if="showMore">
        <!-- Delegat -->
        <div class="d-flex flex-column gap-2 mt-3">
          <div>
            <label class="form-label mb-1">Delegat</label>
            <input v-model="factura.delegat_nume" type="text" class="form-control" placeholder="Nume delegat">
          </div>
          <div>
            <label class="form-label mb-1">Date delegat</label>
            <input v-model="factura.delegat_date" type="text" class="form-control"
              placeholder="CNP / CI / alte detalii">
          </div>
          <div>
            <label class="form-label mb-1">Auto delegat</label>
            <input v-model="factura.delegat_auto" type="text" class="form-control" placeholder="Număr auto">
          </div>
        </div>

        <!-- Intocmit -->
        <div class="d-flex flex-column gap-2 mt-3">
          <div>
            <label class="form-label mb-1">Întocmit de</label>
            <input v-model="factura.intocmit_de" type="text" class="form-control" placeholder="Nume persoană">
          </div>
          <div>
            <label class="form-label mb-1">CNP întocmit</label>
            <input v-model="factura.intocmit_cnp" type="text" class="form-control" placeholder="CNP">
          </div>
        </div>

        <!-- Informatii suplimentare -->
        <div class="mt-3">
          <label class="form-label mb-1">Informații suplimentare</label>
          <textarea v-model="factura.informatii_suplimentare" rows="3" class="form-control"
            placeholder="Note, observații, condiții speciale..."></textarea>
        </div>
      </template>

      <!-- Produse -->
      <div class="mt-3">
        <label class="form-label mb-1">Produse</label>
        <div class="table-responsive">
          <table class="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Denumire</th>
                <th>Cant.</th>
                <th>UM</th>
                <th>Preț</th>
                <th>TVA %</th>
                <th>Valoare</th>
                <th>TVA</th>
                <th>Total</th>
                <th>Cont credit</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(p, idx) in produseLocal">
                <!-- VIEW ROW: compact summary when not editing -->
                <tr v-if="!p._editing" :key="`prod-view-${idx}`">
                  <td>{{ p.pozitie || idx + 1 }}</td>
                  <td>{{ p.denumire }}</td>
                  <td>{{ p.cantitate }}</td>
                  <td>{{ formatUm(p.um) }}</td>
                  <td>{{ p.pret }}</td>
                  <td>{{ p.cota_tva }}</td>
                  <td>{{ p.valoare }}</td>
                  <td>{{ p.valoare_tva }}</td>
                  <td>{{ p.total }}</td>
                  <td>{{ formatContCredit(p.cont_credit) }}</td>
                  <td class="text-end">
                    <button type="button" class="btn btn-xs btn-light-primary me-1" @click="editProdus(idx)">
                      Editează
                    </button>
                    <button type="button" class="btn btn-xs btn-light-danger" @click="removeProdus(idx)">
                      Șterge
                    </button>
                  </td>
                </tr>

                <!-- EDIT ROW: full form card when editing -->
                <tr v-else :key="`prod-edit-${idx}`">
                  <td colspan="10" class="p-0">
                    <div class="border rounded p-3 mb-2 d-flex flex-column gap-3 bg-light-subtle">

                      <!-- ROW 1: DENUMIRE + BADGE # -->
                      <div class="d-flex flex-column">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                          <span class="badge bg-secondary">
                            #{{ p.pozitie || idx + 1 }}
                          </span>
                        </div>
                        <label class="form-label mb-1 fw-bold">Denumire produs</label>
                        <input v-model="p.denumire" type="text" class="form-control form-control-lg"
                          placeholder="Denumirea completă a produsului / serviciului">
                      </div>

                      <!-- ROW 2: # linie + TIP -->
                      <div class="row g-3">
                        <div class="col-6 col-md-3">
                          <label class="form-label mb-1 fw-bold"># linie</label>
                          <input v-model.number="p.pozitie" type="number" min="1" class="form-control form-control-lg">
                        </div>
                        <div class="col-6 col-md-9">
                          <label class="form-label mb-1 fw-bold">Tip</label>
                          <multiselect v-model="p.tip" :options="tipuriProduse" track-by="id" label="text"
                            placeholder="Alege tip" class="w-100" :open-direction="'bottom'" :select-label="''"
                            :deselect-label="''" :selected-label="''" />
                        </div>
                      </div>

                      <!-- ROW 3: GRID WITH BIG FIELDS -->
                      <div class="row g-3">

                        <div class="col-6 col-md-3">
                          <label class="form-label mb-1 fw-bold">Cantitate</label>
                          <input v-model.number="p.cantitate" type="number" min="0" step="0.01"
                            class="form-control form-control-lg" @input="onProdusChanged(p)">
                        </div>

                        <div class="col-6 col-md-3">
                          <label class="form-label mb-1 fw-bold">UM</label>
                          <multiselect v-model="p.um" :options="unitatiMasura" track-by="id" label="text"
                            placeholder="Alege UM" class="w-100 small-text-multiselect" :open-direction="'bottom'"
                            :select-label="''" :deselect-label="''" :selected-label="''" />
                        </div>

                        <div class="col-6 col-md-3">
                          <label class="form-label mb-1 fw-bold">Preț unitar</label>
                          <input v-model.number="p.pret" type="number" min="0" step="0.0001"
                            class="form-control form-control-lg" @input="onProdusChanged(p)">
                        </div>

                        <div class="col-6 col-md-3">
                          <label class="form-label mb-1 fw-bold">TVA (%)</label>
                          <multiselect v-model="p.cota_tva_obj" :options="coteTva" :custom-label="tvaLabel"
                            track-by="id" placeholder="Alege TVA" class="w-100" :open-direction="'bottom'"
                            :select-label="''" :deselect-label="''" :selected-label="''"
                            @update:modelValue="val => onTvaSelected(p, val)" />
                        </div>

                        <div class="col-6 col-md-4">
                          <label class="form-label mb-1 fw-bold">Valoare</label>
                          <input v-model.number="p.valoare" type="number" min="0" step="0.01"
                            class="form-control form-control-lg" @input="onValoareChanged(p)">
                        </div>

                        <div class="col-6 col-md-4">
                          <label class="form-label mb-1 fw-bold">TVA</label>
                          <input v-model.number="p.valoare_tva" type="number" min="0" step="0.01"
                            class="form-control form-control-lg" readonly>
                        </div>

                        <div class="col-6 col-md-4">
                          <label class="form-label mb-1 fw-bold">Total</label>
                          <input v-model.number="p.total" type="number" min="0" step="0.01"
                            class="form-control form-control-lg fw-bold" @input="onTotalChanged(p)">
                        </div>

                        <div class="col-12 col-md-6">
                          <label class="form-label mb-1 fw-bold">Cont credit</label>
                          <multiselect v-model="p.cont_credit_obj" :options="planConturi" :custom-label="pcLabel"
                            track-by="id" placeholder="Alege cont" class="w-100 small-text-multiselect"
                            :open-direction="'bottom'" :select-label="''" :deselect-label="''" :selected-label="''"
                            @update:modelValue="val => onContCreditSelected(p, val)" />
                        </div>

                      </div>

                      <!-- ACTION BUTTONS -->
                      <div class="d-flex justify-content-between align-items-center mt-2">
                        <button type="button" class="btn btn-sm btn-light-danger" @click="removeProdus(idx)">
                          Șterge produs
                        </button>
                        <button type="button" class="btn btn-sm btn-primary" @click="saveProdus(idx)">
                          Salvează produs
                        </button>
                      </div>

                    </div>
                  </td>
                </tr>
              </template>
              <tr v-if="!produseLocal.length">
                <td colspan="10" class="text-muted small text-center py-3">
                  Niciun produs adăugat. Apasă „Adaugă produs”.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-2">
          <button type="button" class="btn btn-sm btn-light-primary fw-bold" @click="addProdus">
            + Adaugă produs
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import axios from 'axios'

export default {
  name: 'AdaugaFacturaIesireVertical',
  components: { Multiselect },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    seriiFacturi: {
      type: Array,
      default: () => []
    },
    listaClienti: {
      type: Array,
      default: () => []
    },
    valute: {
      type: Array,
      default: () => []
    },
    unitatiMasura: {
      type: Array,
      default: () => []
    },
    tipuriProduse: {
      type: Array,
      default: () => []
    },
    coteTva: {
      type: Array,
      default: () => []
    },
    planConturi: {
      type: Array,
      default: () => []
    },
    produse: {
      type: Array,
      default: () => []
    },
    autoInit: {
      type: Boolean,
      default: true
    },
    initialNumar: {
      type: [String, Number],
      default: ''
    },
    soldFormatter: {
      type: Function,
      default: (val) => {
        if (val === null || val === undefined || val === '') return '';
        const n = Number(val) || 0;
        return n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    }
  },
  emits: ['update:modelValue', 'client-selected', 'serie-selected', 'update:produse'],
  data() {
    return {
      showMore: false,
      smartSerieNr: '',
      termenPlataManual: false,
      tipuriDocument: [], // Added for tip document logic
    };
  },

  computed: {
    factura: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      }
    },
    serieObj: {
      get() {
        return (
          this.seriiFacturi.find(s =>
            s.id === this.factura.serie_document ||
            s.value === this.factura.serie_document ||
            s.serie === this.factura.serie_document
          ) || this.factura.serie_document_obj || null
        );
      },
      set(val) {
        this.factura.serie_document = val
          ? (val.id ?? val.value ?? val.serie ?? null)
          : null;
        this.factura.serie_document_obj = val || null;
        this.$emit('serie-selected', val);
      }
    },
    clientObj: {
      get() {
        return (
          this.listaClienti.find(c =>
            c.id === this.factura.client ||
            c.value === this.factura.client ||
            c.id_client === this.factura.client
          ) || this.factura.client_obj || null
        );
      },
      set(val) {
        const id = val
          ? (val.id ?? val.value ?? val.id_client ?? null)
          : null;
        this.factura.client = id;
        this.factura.client_obj = val || null;
        this.$emit('client-selected', val);
      }
    },
    formattedSold() {
      return this.soldFormatter(this.factura.sold);
    },
    tipDocObj: { // Added
      get() {
        return this.tipuriDocument.find(t => t.id === this.factura.id_tip_document) || null;
      },
      set(val) {
        if (val) {
          this.factura.id_tip_document = val.id;
          this.factura.tip_document = val.descriere;
        } else {
          this.factura.id_tip_document = null;
          this.factura.tip_document = 'Factura_iesire'; // fallback
        }
      }
    },
    produseLocal: {
      get() {
        return this.produse;
      },
      set(val) {
        this.$emit('update:produse', val);
      }
    },
    valutaObj: {
      get() {
        return (
          this.valute.find(v =>
            v.id === this.factura.valuta ||
            v.value === this.factura.valuta ||
            v.scurt === this.factura.valuta ||
            v.text === this.factura.valuta
          ) || this.factura.valuta_obj || null
        );
      },
      set(val) {
        const id = val
          ? (val.id ?? val.value ?? val.scurt ?? val.text ?? null)
          : null;
        this.factura.valuta = id;
        this.factura.valuta_obj = val || null;

        // dacă valuta are curs, îl setăm automat
        if (val && typeof val.curs !== 'undefined' && val.curs !== null) {
          const n = Number(val.curs);
          if (!Number.isNaN(n)) {
            this.factura.curs_valutar = n;
          }
        }
      }
    },
  },
  methods: {
    incarcaTipDocument() {
      axios.get('/api/administrare/work/utils/tipuri-documente', { params: { tip: 2 } })
        .then(r => {
          this.tipuriDocument = r.data || [];
          // Set default to 'Factura'
          if (this.tipuriDocument.length > 0) {
            const def = this.tipuriDocument.find(t => t.descriere === 'Factura');
            this.tipDocObj = def || this.tipuriDocument[0];
          } else {
            this.tipDocObj = null;
          }
        })
        .catch(e => console.error("Error fetching types:", e));
    },
    initSmartSerie() {
      // Resolve series text from object or ID
      let sText = '';
      if (this.factura.serie_document_obj) {
        sText = this.serieLabel(this.factura.serie_document_obj);
      } else if (this.factura.serie_document) {
        // Try to find in list
        const found = this.seriiFacturi.find(x => x.id === this.factura.serie_document);
        if (found) sText = this.serieLabel(found);
        else sText = String(this.factura.serie_document);
      }

      if (sText || this.factura.nr_document) {
        const n = (this.factura.nr_document || '').trim()
        this.smartSerieNr = (sText.trim() + ' ' + n).trim()
      } else {
        this.smartSerieNr = ''
      }
    },
    onSmartSerieNrInput() {
      const val = this.smartSerieNr || ''
      let sText = '', nText = '';

      const match = val.match(/^(.*?)(\d+)$/)
      if (match) {
        sText = match[1].trim()
        nText = match[2]
      } else {
        sText = val.trim()
      }

      this.factura.nr_document = nText

      // Try resolving Series ID
      this.factura.serie_document = sText; // Fallback
      this.factura.serie_document_obj = null;

      if (this.seriiFacturi && this.seriiFacturi.length) {
        const found = this.seriiFacturi.find(x =>
          this.serieLabel(x).toLowerCase() === sText.toLowerCase() ||
          (x.serie && x.serie.toLowerCase() === sText.toLowerCase())
        );
        if (found) {
          this.factura.serie_document = found.id;
          this.factura.serie_document_obj = found;
        }
      }
    },
    valutaLabel(option) {
      if (!option) return '';
      if (typeof option !== 'object') return String(option);
      // ex: "EUR (4.97)"
      const base = option.text || option.scurt || option.label || String(option.id ?? option.value ?? '');
      if (option.curs !== undefined && option.curs !== null) {
        return `${base} (${option.curs})`;
      }
      return base;
    },
    serieLabel(option) {
      if (!option) return '';
      if (typeof option !== 'object') return String(option);
      return option.text || option.denumire || option.label || option.serie || String(option.id ?? option.value ?? '');
    },
    clientLabel(option) {
      if (!option) return '';
      // safety check
      if (typeof option !== 'object') return String(option);

      const name = option.nume || option.denumire || option.name || option.label || option.text || '';
      const cui = option.cui || option.CUI || option.cif || option.cod_fiscal || '';
      if (name && cui) return `${name} (${cui})`;
      return name || String(cui || '');
    },
    tvaLabel(option) {
      if (!option) return '';
      if (typeof option !== 'object') return String(option);

      const v = (option.cota_tva != null) ? option.cota_tva : (option.value ?? option.id ?? '');
      return String(v);
    },
    onTvaSelected(p, val) {
      if (!p) return;
      p.cota_tva_obj = val || null;
      const t = val ? (val.cota_tva ?? val.value ?? val.id) : 0;
      p.cota_tva = (t != null && t !== '') ? Number(t) : 0;
      this.onProdusChanged(p);
    },
    pcLabel(option) {
      if (!option) return '';
      const cod = option.cod_complet ?? option.cod ?? option.value ?? option.id ?? '';
      const den = option.descriere ?? option.denumire ?? option.text ?? option.label ?? '';
      return den ? `${cod} | ${den}` : String(cod);
    },
    onContCreditSelected(p, val) {
      if (!val) {
        p.cont_credit = null;
        p.cont_credit_obj = null;
      } else {
        const cod = val.cod_complet ?? val.cod ?? val.value ?? val.id;
        p.cont_credit = cod ? String(cod) : null;
        p.cont_credit_obj = val;
      }
    },
    formatContCredit(idVal) {
      if (!idVal) return '';
      if (!this.planConturi || !this.planConturi.length) return idVal;
      const found = this.planConturi.find(x => x.id === idVal || x.cod_complet === idVal);
      if (found) {
        return this.pcLabel(found);
      }
      return idVal;
    },
    hydrateProdusRefs(p) {
      if (!p) return;

      // TVA object from numeric
      if (!p.cota_tva_obj && Array.isArray(this.coteTva) && this.coteTva.length) {
        const t = (p.cota_tva != null && p.cota_tva !== '') ? Number(p.cota_tva) : null;
        if (t !== null && !Number.isNaN(t)) {
          p.cota_tva_obj = this.coteTva.find(x => Number(x.cota_tva) === t || Number(x.value) === t) || null;
        }
      }
      // If we have object but numeric missing, keep numeric in sync
      if (p.cota_tva_obj && (p.cota_tva == null || p.cota_tva === '')) {
        const t = p.cota_tva_obj.cota_tva ?? p.cota_tva_obj.value ?? p.cota_tva_obj.id;
        if (t != null) p.cota_tva = Number(t);
      }

      // Cont Credit object from numeric/string
      if (!p.cont_credit_obj && Array.isArray(this.planConturi) && this.planConturi.length) {
        if (p.cont_credit) {
          const cc = String(p.cont_credit);
          p.cont_credit_obj = this.planConturi.find(x => String(x.cod_complet ?? x.id ?? '') === cc) || null;
        }
      }
      // If we have object but code missing
      if (p.cont_credit_obj && !p.cont_credit) {
        p.cont_credit = String(p.cont_credit_obj.cod_complet ?? p.cont_credit_obj.id);
      }
    },
    hydrateAllProduseRefs() {
      if (!Array.isArray(this.produseLocal)) return;
      this.produseLocal.forEach(p => this.hydrateProdusRefs(p));
    },
    toNumber(val, fallback = 0) {
      const n = Number(val);
      return Number.isNaN(n) ? fallback : n;
    },
    recalcProdus(p) {
      if (!p) return;
      const cant = this.toNumber(p.cantitate, 0);
      const pret = this.toNumber(p.pret, 0);
      const cota = this.toNumber(p.cota_tva, 0);

      const valoare = cant * pret;
      const valoare_tva = valoare * cota / 100;
      const total = valoare + valoare_tva;

      p.valoare = Number.isFinite(valoare) ? Number(valoare.toFixed(2)) : 0;
      p.valoare_tva = Number.isFinite(valoare_tva) ? Number(valoare_tva.toFixed(2)) : 0;
      p.total = Number.isFinite(total) ? Number(total.toFixed(2)) : 0;
    },
    onProdusChanged(p) {
      this.recalcProdus(p);
    },
    onValoareChanged(p) {
      const val = this.toNumber(p.valoare, 0);
      const cant = this.toNumber(p.cantitate, 1);
      const cota = this.toNumber(p.cota_tva, 0);

      const pret = cant !== 0 ? val / cant : 0;
      const valoare_tva = val * cota / 100;
      const total = val + valoare_tva;

      p.pret = Number.isFinite(pret) ? Number(pret.toFixed(4)) : 0;
      p.valoare_tva = Number.isFinite(valoare_tva) ? Number(valoare_tva.toFixed(2)) : 0;
      p.total = Number.isFinite(total) ? Number(total.toFixed(2)) : 0;
    },
    onTotalChanged(p) {
      const tot = this.toNumber(p.total, 0);
      const cant = this.toNumber(p.cantitate, 1);
      const cota = this.toNumber(p.cota_tva, 0);

      const valoare = tot / (1 + cota / 100);
      const pret = cant !== 0 ? valoare / cant : 0;
      const valoare_tva = tot - valoare;

      p.valoare = Number.isFinite(valoare) ? Number(valoare.toFixed(2)) : 0;
      p.pret = Number.isFinite(pret) ? Number(pret.toFixed(4)) : 0;
      p.valoare_tva = Number.isFinite(valoare_tva) ? Number(valoare_tva.toFixed(2)) : 0;
    },
    resolveDefaultTip() {
      // Prefer explicit "G" / "Articole Generale" option if present, otherwise fallback to first option
      const opts = Array.isArray(this.tipuriProduse) ? this.tipuriProduse : [];
      if (!opts.length) return 1;

      const byText = opts.find(o => {
        const t = String(o?.text ?? o?.label ?? o?.denumire ?? '').toLowerCase();
        return t.includes('articole generale') || t.startsWith('g');
      });
      if (byText) return byText;

      const byId1 = opts.find(o => Number(o?.id ?? o?.value) === 1);
      if (byId1) return byId1;

      return opts[0];
    },
    resolveDefaultUm() {
      const opts = Array.isArray(this.unitatiMasura) ? this.unitatiMasura : [];
      if (!opts.length) return null;

      // Prefer id=1 if exists
      const byId1 = opts.find(o => Number(o?.id ?? o?.value) === 1);
      if (byId1) return byId1;

      // Fallback by text match "Bucata"
      const byText = opts.find(o => String(o?.text ?? o?.label ?? o?.denumire ?? '').toLowerCase().includes('buc'));
      if (byText) return byText;

      return opts[0];
    },
    addProdus() {
      const nextPoz = (this.produseLocal?.length || 0) + 1;

      const defTip = this.resolveDefaultTip();
      const defUm = this.resolveDefaultUm();

      const nou = {
        pozitie: nextPoz,
        denumire: '',
        tip: defTip || 1,
        cota_tva: 21,
        um: defUm || null,
        cantitate: 1,
        pret: 0,
        valoare: 0,
        valoare_tva: 0,
        total: 0,
        cont_credit: null,
        _editing: true,
      };
      this.recalcProdus(nou);
      const arr = Array.isArray(this.produseLocal) ? [...this.produseLocal, nou] : [nou];
      this.produseLocal = arr;
    },
    removeProdus(idx) {
      if (!Array.isArray(this.produseLocal)) return;
      const arr = [...this.produseLocal];
      arr.splice(idx, 1);
      this.produseLocal = arr;
    },
    editProdus(idx) {
      if (!Array.isArray(this.produseLocal)) return;
      const arr = [...this.produseLocal];
      if (arr[idx]) {
        arr[idx]._editing = true;
        this.produseLocal = arr;
      }
    },
    async saveProdus(idx) {
      if (!Array.isArray(this.produseLocal)) return;
      const arr = [...this.produseLocal];
      if (arr[idx]) {
        // basic sanity: dacă nu are denumire, nu închidem formularul
        if (!arr[idx].denumire || String(arr[idx].denumire).trim() === '') {
          return;
        }

        // Verifica Cont Credit
        if (!arr[idx].cont_credit && !arr[idx].cont_credit_obj) {
          const result = await Swal.fire({
            title: 'Cont credit nesetat',
            text: 'Acest produs nu are Cont Credit setat. Salvați produsul?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Da, salvează',
            cancelButtonText: 'Nu, continuă editarea'
          });

          if (!result.isConfirmed) {
            return;
          }
        }

        arr[idx]._editing = false;
        this.produseLocal = arr;
      }
    },
    formatUm(um) {
      if (!um) return '';
      if (typeof um === 'string') return um;
      if (typeof um === 'object') {
        return um.text || um.scurt || um.label || String(um.id ?? um.value ?? '');
      }
      return String(um);
    },
    openNativePicker(which) {
      if (which === 'emitere' && this.$refs.dateEmitereRef) {
        this.$refs.dateEmitereRef.showPicker?.();
      }
      if (which === 'scadenta' && this.$refs.dateScadentaRef) {
        this.$refs.dateScadentaRef.showPicker?.();
      }
    },

  },
  mounted() {
    this.hydrateAllProduseRefs();

    this.incarcaTipDocument();
    if (this.autoInit) {
      if (this.valute.length && !this.factura.valuta) {
        const first = this.valute[0];
        this.valutaObj = first;
      }

      if (!this.factura.serie_document && this.seriiFacturi.length) {
        const last = this.seriiFacturi[this.seriiFacturi.length - 1];
        this.serieObj = last;
      }

      if (!this.factura.nr_document) {
        if (this.initialNumar) {
          this.factura.nr_document = String(this.initialNumar);
        } else if (this.factura.serie_document_obj && this.factura.serie_document_obj.last_nr) {
          const next = Number(this.factura.serie_document_obj.last_nr) + 1;
          if (!Number.isNaN(next)) {
            this.factura.nr_document = String(next);
          }
        }
      }

      // setăm automat data emiterii la data de azi dacă nu e deja setată
      if (!this.factura.data_emitere) {
        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        this.factura.data_emitere = today;
      }

      // Auto-add one empty product line if none exist
      if (!this.produseLocal || !this.produseLocal.length) {
        this.addProdus();
      }
    }
  },

  watch: {
    'factura.id': {
      handler() {
        this.initSmartSerie();
      },
      immediate: true
    },
    'factura.data_emitere': {
      handler(newVal) {
        // If due date is empty OR we are in auto-sync mode (not manual), sync it
        if (newVal && (!this.factura.termen_plata || !this.termenPlataManual)) {
          this.factura.termen_plata = newVal;
        }
      },
      immediate: true
    },
    seriiFacturi(newVal) {
      if (this.autoInit && newVal && newVal.length) {
        if (!this.factura.serie_document) {
          const last = newVal[newVal.length - 1];
          this.serieObj = last;
        }

        if (!this.factura.nr_document) {
          if (this.initialNumar) {
            this.factura.nr_document = String(this.initialNumar);
          } else if (this.factura.serie_document_obj?.last_nr) {
            const next = Number(this.factura.serie_document_obj.last_nr) + 1;
            if (!Number.isNaN(next)) {
              this.factura.nr_document = String(next);
            }
          }
        }
      }
    },
    coteTva() {
      this.hydrateAllProduseRefs();
    },
    produse: {
      deep: true,
      handler() {
        this.hydrateAllProduseRefs();
      }
    }
  }
}
</script>

<style scoped>
.adauga-factura-iesire-vertical {
  min-width: 320px;
}

.client-multiselect .multiselect__tags {
  border-radius: 0.475rem;
}

.multiselect {
  min-width: 0;
}

/* Force dark text inside vue-multiselect for visibility */
.multiselect :deep(.multiselect__input),
.multiselect :deep(.multiselect__single),
.multiselect :deep(.multiselect__tag),
.multiselect :deep(.multiselect__placeholder),
.multiselect :deep(.multiselect__content) {
  color: #181C32 !important;
  /* text vizibil pe fundal deschis */
}

.multiselect :deep(.multiselect__tags) {
  background-color: #fff;
  color: #181C32 !important;
}

input[type="date"].form-control {
  color: #181C32 !important;
  background-color: #ffffff !important;
}

.date-picker-btn {
  position: absolute;
  top: 50%;
  right: 0.75rem;
  transform: translateY(-50%);
  padding: 0.25rem 0.4rem;
}

/* Stil pentru UM scris mai mic */
.small-text-multiselect :deep(.multiselect__single),
.small-text-multiselect :deep(.multiselect__option) {
  font-size: 0.85rem !important;
  white-space: normal !important;
  line-height: 1.25 !important;
}
</style>
